import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [toolkitId, setToolkitId] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setToolkitId(data.toolkit_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("failed");
          return;
        }
      } catch {}
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="fade-in min-h-[70vh] flex items-center justify-center px-6">
      <div className="card-surface p-12 max-w-lg w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto text-teal-400 animate-spin mb-4" size={40} />
            <h1 className="font-display text-3xl mb-2">Confirming your payment…</h1>
            <p className="text-neutral-400 text-sm">This takes just a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-teal-400 mb-4" size={48} />
            <h1 className="font-display text-3xl mb-2">Unlocked.</h1>
            <p className="text-neutral-400 mb-8">Your toolkit is in your library. We also sent a copy to your email.</p>
            <div className="flex gap-3 justify-center">
              {toolkitId === "the_soundtrack" || toolkitId === "everything_bundle" ? (
                <Link to={toolkitId === "everything_bundle" ? "/dashboard" : "/soundtrack"} data-testid="payment-open-toolkit-btn" className="btn-primary px-5 py-2.5">
                  {toolkitId === "everything_bundle" ? "Open library" : "Open soundtrack"}
                </Link>
              ) : toolkitId && toolkitId !== "recovery_bundle" ? (
                <Link to={`/toolkit/${toolkitId}`} data-testid="payment-open-toolkit-btn" className="btn-primary px-5 py-2.5">
                  Open toolkit
                </Link>
              ) : (
                <Link to="/dashboard" data-testid="payment-open-toolkit-btn" className="btn-primary px-5 py-2.5">
                  Open library
                </Link>
              )}
              <Link to="/dashboard" className="btn-ghost px-5 py-2.5">Dashboard</Link>
            </div>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout") && (
          <>
            <h1 className="font-display text-3xl mb-2">Something's off.</h1>
            <p className="text-neutral-400 mb-8">
              {status === "timeout"
                ? "We couldn't confirm in time. If the charge went through, your library will update shortly."
                : "The payment didn't complete."}
            </p>
            <Link to="/studio" className="btn-primary px-5 py-2.5">Back to The Studio</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
